import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator } from "lucide-react";

const FleetEmissionAnalytics = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const emissionData = [
    {
      id: "co2",
      title: "Carbon Dioxide (CO₂)",
      formula: "CO₂ (kg) = Σ(Fuel Consumption (kg) × CO₂ Emission Factor)",
      description: "The primary greenhouse gas emitted from burning fossil fuels. Calculated daily per vessel.",
      calculationDetails: [
        "CO₂ from HFO = Heavy Fuel Oil (kg) × CF_C02 (from FuelTable)",
        "CO₂ from LFO = Light Fuel Oil (kg) × CF_C02 (from FuelTable)", 
        "CO₂ from MGO = Marine Gas Oil (kg) × CF_C02 (from FuelTable)",
        "Daily Average = Total CO₂ / Number of Days",
        "Final Unit: MT/day (Metric Tons per day)"
      ],
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "ch4",
      title: "Methane (CH₄)",
      formula: "CH₄ (kg) = Σ(Fuel Consumption (kg) × CH₄ Emission Factor)",
      description: "A potent greenhouse gas with high global warming potential. Calculated using fuel-specific factors.",
      calculationDetails: [
        "CH₄ Emission Factor = CF_CH4 from FuelTable for each fuel type",
        "CH₄ from HFO = HFO Consumption (kg) × CF_CH4_HFO",
        "CH₄ from LFO = LFO Consumption (kg) × CF_CH4_LFO",
        "CH₄ from MGO = MGO Consumption (kg) × CF_CH4_MGO",
        "Daily Average = Total CH₄ / Number of Days",
        "Final Unit: MT/day"
      ],
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    },
    {
      id: "n2o",
      title: "Nitrous Oxide (N₂O)",
      formula: "N₂O (kg) = Σ(Fuel Consumption (kg) × N₂O Emission Factor)",
      description: "Another very potent greenhouse gas from engine combustion processes.",
      calculationDetails: [
        "N₂O Emission Factor = CF_N20 from FuelTable for each fuel type",
        "N₂O from HFO = HFO Consumption (kg) × CF_N20_HFO",
        "N₂O from LFO = LFO Consumption (kg) × CF_N20_LFO",
        "N₂O from MGO = MGO Consumption (kg) × CF_N20_MGO",
        "Daily Average = Total N₂O / Number of Days",
        "Final Unit: MT/day"
      ],
      borderColor: "border-pink-200",
      bgColor: "bg-pink-50",
      textColor: "text-pink-700",
    },
    {
      id: "co2e",
      title: "Carbon Dioxide Equivalent (CO₂e)",
      formula: "CO₂e (kg) = CO₂ + (CH₄ × 28) + (N₂O × 298)",
      description: "A single metric that combines all greenhouse gases based on their global warming potential over 100 years.",
      calculationDetails: [
        "Global Warming Potential (GWP) factors:",
        "Methane (CH₄): 28 times stronger than CO₂",
        "Nitrous Oxide (N₂O): 298 times stronger than CO₂",
        "Calculation: (CO₂ × 1) + (CH₄ × 28) + (N₂O × 298)",
        "Daily Average = Total CO₂e / Number of Days",
        "Final Unit: MT/day"
      ],
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
    {
      id: "ghg",
      title: "Greenhouse Gas Intensity",
      formula: "GHG Intensity (g CO₂e/MJ) = Reward Factor × (WtT + TtW)",
      description: "Well-to-Wake GHG intensity measured in grams of CO₂ equivalent per megajoule of energy consumed.",
      calculationDetails: [
        "WtT (Well-to-Tank): Emissions from fuel production and transport",
        "TtW (Tank-to-Wake): Emissions from fuel combustion",
        "Reward Factor: Currently set to 1.0",
        "Based on fuel-specific LCV (Lower Calorific Value) and RFNBO factors",
        "Accounts for carbon slip and shore power energy",
        "Compliant with IMO DCS and EU MRV methodologies"
      ],
      additionalInfo: "This is an intensity metric (g CO₂e/MJ), not a total emission value. It measures efficiency of energy use.",
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "nox",
      title: "Nitrogen Oxides (NOₓ)",
      formula: "NOₓ (kg) = Σ(Fuel Consumption by Mode × NOₓ Emission Factor)",
      description: "Calculated based on engine type, operational mode, and specific NOₓ emission factors.",
      calculationDetails: [
        "Engine Types: ME (Main Engine), AE (Auxiliary Engine), BL (Boiler)",
        "Operational Modes: At Sea, At Anchorage, At Port",
        "Fuel Types: HSFO, LSFO, MDO, LSMDO",
        "Emission Factors vary by configuration:",
        "• ME HSFO/LSFO: 78.46 g/kg fuel",
        "• ME MDO/LSMDO: 73.75 g/kg fuel", 
        "• AE HSFO/LSFO: 49.37 g/kg fuel",
        "• AE MDO/LSMDO: 46.37 g/kg fuel",
        "• BL HSFO/LSFO: 6.89 g/kg fuel",
        "• BL MDO/LSMDO: 6.47 g/kg fuel",
        "Daily Average = Total NOₓ / Number of Days",
        "Final Unit: MT/day"
      ],
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "sox",
      title: "Sulfur Oxides (SOₓ)",
      formula: "SOₓ (kg) = Σ(Fuel Consumption (t) × Sulfur Content (%) × 2 × 0.97753 × 10)",
      description: "Calculated from fuel sulfur content using the conversion factor 2 × 0.97753.",
      calculationDetails: [
        "For each fuel type (HSFO, LSFO, MDO, LSMDO):",
        "SOₓ = Fuel Consumption (tonnes) × Sulfur Content (%) × 2 × 0.97753 × 10",
        "Conversion factor: 2 × 0.97753 accounts for molecular weight ratio (S to SO₂)",
        "Multiplied by 10 to convert from % to kg per tonne",
        "Sulfur content measured from vessel reports",
        "Daily Average = Total SOₓ / Number of Days",
        "Final Unit: MT/day"
      ],
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "pm",
      title: "Particulate Matter (PM)",
      formula: "PM (kg) = Σ((Fuel Consumption / SFOC) × PM10_Kwh × 1000)",
      description: "Calculated using Specific Fuel Oil Consumption (SFOC) and sulfur-content-dependent PM emission factors.",
      calculationDetails: [
        "PM10_Kwh = 1.35 + SFOC × 7 × 0.02247 × (Sulfur Content/100 - 0.0246)",
        "SFOC values from SFOC table (e.g., HFO: ~200 g/kWh+)",
        "PM (kg) = (Fuel Consumption (g) / SFOC (g/kWh)) × PM10_Kwh / 1000",
        "Considers sulfur content from latest vessel report",
        "Separate calculations for HFO/LFO and MGO fuels",
        "Daily Average = Total PM / Number of Days",
        "Final Unit: MT/day"
      ],
      borderColor: "border-gray-300",
      bgColor: "bg-gray-100",
      textColor: "text-gray-700",
    },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#fce3d2] rounded-lg">
              <Calculator className="w-6 h-6 text-[#d35400]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Fleet Emission Analytics
              </h2>
              <p className="text-gray-600 text-sm">
                Methodology and formulas for emission calculations
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {emissionData.map((item) => (
                <div
                  key={item.id}
                  className={`p-5 rounded-lg border ${item.borderColor} ${item.bgColor}`}
                >
                  <h4 className="font-semibold text-gray-800 mb-3">
                    {item.title}
                  </h4>
                  <div className="p-3 bg-white rounded border border-gray-200 mb-3">
                    <p className={`font-semibold ${item.textColor}`}>
                      {item.formula}
                    </p>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">
                    {item.description}
                  </p>
                  
                  {item.calculationDetails && (
                    <div className="mt-3 p-2 bg-white rounded border border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Calculation Details:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {item.calculationDetails.map((detail, index) => (
                          <li key={index}>• {detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {item.additionalInfo && (
                    <div className="mt-3 p-2 bg-white rounded border border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Additional Information:</p>
                      <p className="text-xs text-gray-600 italic">
                        {item.additionalInfo}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-3">Data Sources & References:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="font-semibold mb-1">Fuel Data</p>
                  <p>Emission factors from FuelTable (CF_C02, CF_CH4, CF_N20, LCV, CO2eq_WtT)</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="font-semibold mb-1">Vessel Data</p>
                  <p>VesselEmissionRawData for SOₓ, NOₓ, PM calculations and operational data</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="font-semibold mb-1">Performance Data</p>
                  <p>VesselPerformance for fuel consumption (HFO, LFO, MGO, shore power)</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FleetEmissionAnalytics;